// Single-use, module-scoped handoff so the gallery's "Shuffle it" CTA can tell
// the editor to open already in room-pick (spotlight) mode, rather than opening
// in its default state and transitioning a tick later.
//
// Deliberately NOT React state, for two reasons:
//
//   1. The gallery unmounts during the navigation that performs the handoff,
//      so shared component state cannot carry the intent across.
//   2. The editor must read the intent during its useState lazy init so pick
//      mode is armed on the very FIRST render. Anything that lands a tick later
//      paints the default editor first — the visible "flash" this exists to
//      avoid. The editor's existing loading veil covers the mount, so an
//      intent applied at first render is never seen as a transition.
//
// Reads are split into peek + consume because BrowserRouter runs with
// v7_startTransition, meaning the editor's first render can be discarded and
// replayed. peek() is therefore side-effect free and safe to call from a lazy
// init; consume() runs from a mount effect, once a render has committed.
//
// Keyed by layout uuid so an intent can never arm the editor for a different
// layout, and TTL'd so an intent written by a navigation that never reached the
// editor cannot survive to arm a later, unrelated editor session. Both guards
// fail open: a miss simply means the editor opens exactly as it does today.

const INTENT_TTL_MS = 30000;

let pending = null; // { uuid: string, at: number }

const matches = (uuid) => {
  if (!pending || !uuid) return false;
  if (Date.now() - pending.at > INTENT_TTL_MS) {
    pending = null;
    return false;
  }
  return pending.uuid === String(uuid);
};

export const setPendingShuffleIntent = (uuid) => {
  if (!uuid) return;
  pending = { uuid: String(uuid), at: Date.now() };
};

export const peekPendingShuffleIntent = (uuid) => matches(uuid);

export const consumePendingShuffleIntent = (uuid) => {
  if (!matches(uuid)) return false;
  pending = null;
  return true;
};

// The editor's first-render decision, kept here so it is directly testable
// rather than reimplemented inside a 17k-line component. Room-pick mode is a
// Make Magic affordance and hides the primary FAB row, so it must never be
// armed for a partner without the feature.
export const shouldArmPickModeOnOpen = ({ allowMakeMagic, layoutKey }) =>
  Boolean(allowMakeMagic) && peekPendingShuffleIntent(layoutKey);

export const clearPendingShuffleIntent = () => {
  pending = null;
};
